/**
 * コレクションフィルター（v2）の処理
 */

const MOBILE_BREAKPOINT = 1080;
const FILTER_INPUT_DEBOUNCE_MS = 250;
const MOBILE_COUNT_DEBOUNCE_MS = 300;

const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT;

const getElement = (id) => document.getElementById(id);
const getElements = () => ({
  filterBtn: getElement("filter-v2-btn"),
  filterUnit: getElement("filter-v2-unit"),
  prevBtn: getElement("filter-v2-title-reset"),
  applyBtn: getElement("filter-v2-btn-apply"),
  featuredProductsSection: getElement("collection-featured-products"),
  featuredProductsIndex: getElement("collection-index-featured-products"),
  form: getElement("filter-v2-form"),
  loadingIcon: document.querySelector(".icon-svg-loading"),
  body: document.body,
  pageContainer: getElement("PageContainer"),
  productSortDom: getElement("filter-v2-products-sort-selectbox"),
  productCountEl: getElement("filter-v2-product-count"),
});

let elements = {};

const FILTER_NAME_MAPPING = {
  tag: "filter.p.tag",
  series: "filter.p.m.filter.series",
  functions: "filter.p.m.filter.functions",
  shape: "filter.p.m.filter.shape",
  type: "filter.p.m.filter.type",
  playTime: "filter.p.m.filter.play_time",
  scene: "filter.p.m.filter.scene",
  output: "filter.p.m.filter.output",
  capacity: "filter.p.m.filter.capacity",
  numberOfPorts: "filter.p.m.filter.number_of_ports",
  portTypes: "filter.p.m.filter.port_types",
  chargingTypeFunctions: "filter.p.m.filter.charging_type_functions",
  length: "filter.p.m.filter.length_multiple",
  kinds: "filter.p.m.filter.kinds",
  inputs: "filter.p.m.filter.inputs",
  acFixedOutput: "filter.p.m.filter.ac_fixed_output",
  batteryMaterial: "filter.p.m.filter.battery_material",
  stationType: "filter.p.m.filter.station_type",
  waterWipeType: "filter.p.m.filter.water_wipe_type",
  resolution: "filter.p.m.filter.resolution",
  vendor: "filter.p.vendor",
  luminance: "filter.p.m.filter.luminance",
};

/* ========================================
 * AbortController（fetch 競合制御）
 * ======================================== */

let currentFetchController = null;
let currentCountController = null;

const abortableFetch = (url, controller) =>
  fetch(url, {
    headers: { Accept: "text/html" },
    credentials: "same-origin",
    signal: controller.signal,
  });

/* ========================================
 * モーダル開閉（ch-plugin 非依存）
 * ======================================== */

const closeModal = (cancel = false) => {
  elements.filterUnit?.classList.remove("active");
  const chPlugin = getElement("ch-plugin");
  if (chPlugin) chPlugin.classList.remove("hide");
  elements.body.classList.remove("is-fixed");
  if (cancel) {
    setInitialValues();
    updateProductCountDisplay();
  }
  updateClearLinkVisibility();
};

const displayModal = () => {
  const unit = elements.filterUnit;
  if (!unit) return;
  const isOpening = !unit.classList.contains("active");
  unit.classList.toggle("active");
  const chPlugin = getElement("ch-plugin");
  if (chPlugin) chPlugin.classList.toggle("hide");
  elements.body.classList.toggle("is-fixed");
  if (isOpening) {
    setInitialValues();
    updateProductCountDisplay();
  }
  updateClearLinkVisibility();
};

/* ========================================
 * URL / フォームパラメータ
 * ======================================== */

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const filterParams = urlParams.toString();
  return filterParams ? filterParams.split("&") : [];
};

const IGNORED_PARAMS = new Set(["type", "sort_by", "q"]);
const PRICE_DEFAULTS = { "filter.v.price.gte": "1", "filter.v.price.lte": "10000000" };
const PRICE_STEPS = [1, 2000, 4000, 5000, 7000, 10000, 15000, 20000, 30000, 50000, 70000, 100000, 150000, 200000, 300000, 10000000];

const isFilterParam = (key, value) => {
  if (key === "q") return value && value !== "*";
  if (key === "filter.v.availability") return false;
  if (key in PRICE_DEFAULTS) return value !== PRICE_DEFAULTS[key];
  if (key.startsWith("filter.p.") || key.startsWith("filter.v.")) return true;
  return false;
};

/**
 * URL ベースでフィルターが選択されているか
 */
const hasFilterParams = () => {
  const params = new URLSearchParams(window.location.search);
  for (const [key, value] of params) {
    if (isFilterParam(key, value)) return true;
  }
  return false;
};

/**
 * フォーム状態ベースでフィルターが選択されているか（スマホモーダル用）
 */
const hasFormFilterParams = () => {
  if (!elements.form) return false;
  const formData = new FormData(elements.form);
  for (const [key, value] of formData) {
    if (isFilterParam(key, value)) return true;
  }
  return false;
};

/**
 * 条件をクリアリンクの表示/非表示を更新
 */
const updateClearLinkVisibility = () => {
  const clearElements = document.querySelectorAll(
    ".filter-v2-link-clear, .filter-v2-clear-pc"
  );
  const isModalOpen = elements.filterUnit?.classList.contains("active");
  const show = isModalOpen && isMobile() ? hasFormFilterParams() : hasFilterParams();
  clearElements.forEach((el) => {
    el.classList.toggle("filter-v2-clear-hidden", !show);
  });
};

/* ========================================
 * アクティブフィルターのチップ表示
 * ======================================== */

const TAG_DISPLAY_MAP = {
  sale: "セール対象製品",
  has_privilege: "購入特典あり",
  new_iphone: "新型iPhoneにおすすめ",
  eol: "販売終了",
  new: "新製品",
  popular_product: "人気製品",
  bulk_cp: "まとめ買い対象",
};

const getChipDisplayValue = (key, rawValue) => {
  const decoded = decodeURIComponent(rawValue);
  if (key === "filter.p.tag" && TAG_DISPLAY_MAP[decoded]) {
    return TAG_DISPLAY_MAP[decoded];
  }
  if (key.startsWith("filter.v.price")) {
    return `¥${Number(rawValue).toLocaleString()}`;
  }
  return decoded;
};

const renderActiveChips = () => {
  const container = getElement("filter-v2-active-chips");
  if (!container) return;

  const params = new URLSearchParams(window.location.search);
  const chips = [];

  for (const [key, value] of params) {
    if (key === "filter.v.availability") continue;
    if (!isFilterParam(key, value)) continue;
    chips.push({ key, value, text: getChipDisplayValue(key, value) });
  }

  if (chips.length === 0) {
    container.innerHTML = "";
    return;
  }

  const baseUrl = new URL(window.location.href);
  const chipHtml = chips
    .map((chip) => {
      const removeUrl = new URL(baseUrl);
      const newParams = new URLSearchParams(removeUrl.search);
      const allValues = newParams.getAll(chip.key);
      newParams.delete(chip.key);
      allValues
        .filter((v) => v !== chip.value)
        .forEach((v) => newParams.append(chip.key, v));
      removeUrl.search = newParams.toString();
      return `<a href="${removeUrl.toString()}" class="filter-v2-chip" data-chip-url="${removeUrl.toString()}"><span class="filter-v2-chip-text">${chip.text}</span><span class="filter-v2-chip-remove">×</span></a>`;
    })
    .join("");

  container.innerHTML = chipHtml;
};

/* ========================================
 * 商品件数の表示
 * ======================================== */

const getCountContainer = (doc = document) =>
  doc.querySelector(".collection-products-v2-inner") ??
  doc.querySelector(".search-v2-inner");

const updateProductCountDisplay = () => {
  const inner = getCountContainer();
  const count = inner?.getAttribute("data-product-count") ?? "0";
  if (elements.productCountEl) {
    elements.productCountEl.textContent = `（${count}件）`;
  }
};

const fetchAndUpdateProductCount = async (url) => {
  const countEl = elements.productCountEl;
  if (!countEl) return;

  if (currentCountController) currentCountController.abort();
  currentCountController = new AbortController();

  countEl.classList.add("is-loading");
  try {
    const response = await abortableFetch(url, currentCountController);
    if (!response.ok) return;
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const inner = getCountContainer(doc);
    const count = inner?.getAttribute("data-product-count") ?? "0";
    countEl.classList.remove("is-loading");
    countEl.textContent = `（${count}件）`;
    countEl.classList.add("is-updated");
    countEl.addEventListener(
      "animationend",
      () => countEl.classList.remove("is-updated"),
      { once: true }
    );
  } catch (e) {
    if (e.name !== "AbortError") countEl.classList.remove("is-loading");
  }
};

/* ========================================
 * フォーム初期値設定
 * ======================================== */

const replaceInputValue = (text) =>
  text.includes("+") ? text.replaceAll("+", " ") : text;

const findMatchedData = (paramValues, filterName, inputValue) =>
  paramValues.find(
    (param) =>
      param.name.includes(filterName) &&
      replaceInputValue(param.value) === inputValue
  );

const setInputInitialValues = (inputs, paramValues) => {
  inputs.forEach((input) => {
    const inputName = input.getAttribute("name");
    if (!inputName) return;

    if (input.type === "checkbox" || input.type === "radio") {
      input.checked = false;
    }

    if (inputName.includes("filter.p.tag")) {
      const tagMatchedData = findMatchedData(paramValues, "filter.p.tag", input.value);
      if (tagMatchedData) {
        input.checked = true;
        return;
      }
    }

    const paramData = paramValues.find((param) => param.name === inputName);
    if (paramData) {
      if (inputName === "filter.v.availability" && paramData.value === "1") {
        input.checked = true;
      } else if (inputName === "sort_by" && input.value === paramData.value) {
        input.checked = true;
      } else if (inputName === "q") {
        input.value = paramData.value;
      }
    }

    Object.values(FILTER_NAME_MAPPING).forEach((filterName) => {
      if (inputName === filterName) {
        const matchedData = findMatchedData(paramValues, filterName, input.value);
        if (matchedData) input.checked = true;
      }
    });
  });
};

/* ========================================
 * Price Range Slider
 * ======================================== */

const formatPriceLabel = (value) => {
  if (value <= 1) return "下限なし";
  if (value >= 10000000) return "上限なし";
  return `¥${value.toLocaleString()}`;
};

const priceToStep = (price) => {
  const num = Number(price);
  let closest = 0;
  let minDiff = Infinity;
  for (let i = 0; i < PRICE_STEPS.length; i++) {
    const diff = Math.abs(PRICE_STEPS[i] - num);
    if (diff < minDiff) { minDiff = diff; closest = i; }
  }
  return closest;
};

const updatePriceSliderUI = (container) => {
  const minInput = container.querySelector(".price-slider-input-min");
  const maxInput = container.querySelector(".price-slider-input-max");
  if (!minInput || !maxInput) return;

  let minStep = parseInt(minInput.value);
  let maxStep = parseInt(maxInput.value);

  if (minStep > maxStep) {
    if (minInput === document.activeElement) {
      minInput.value = maxStep;
      minStep = maxStep;
    } else {
      maxInput.value = minStep;
      maxStep = minStep;
    }
  }

  const range = container.querySelector(".price-slider-range");
  const labelMin = container.querySelector(".price-slider-label-min");
  const labelMax = container.querySelector(".price-slider-label-max");
  const hiddenGte = container.querySelector('input[name="filter.v.price.gte"]');
  const hiddenLte = container.querySelector('input[name="filter.v.price.lte"]');
  const last = PRICE_STEPS.length - 1;
  const leftPct = (minStep / last) * 100;
  const rightPct = (maxStep / last) * 100;

  if (range) { range.style.left = leftPct + "%"; range.style.width = (rightPct - leftPct) + "%"; }
  if (labelMin) labelMin.textContent = formatPriceLabel(PRICE_STEPS[minStep]);
  if (labelMax) labelMax.textContent = formatPriceLabel(PRICE_STEPS[maxStep]);
  if (hiddenGte) hiddenGte.value = PRICE_STEPS[minStep];
  if (hiddenLte) hiddenLte.value = PRICE_STEPS[maxStep];
};

const setPriceSliderInitialValues = () => {
  const container = getElement("filter-v2-price-range");
  if (!container) return;
  const minInput = container.querySelector(".price-slider-input-min");
  const maxInput = container.querySelector(".price-slider-input-max");
  if (!minInput || !maxInput) return;

  const params = new URLSearchParams(window.location.search);
  const gteValue = params.get("filter.v.price.gte");
  const lteValue = params.get("filter.v.price.lte");
  minInput.value = gteValue ? priceToStep(gteValue) : 0;
  maxInput.value = lteValue ? priceToStep(lteValue) : PRICE_STEPS.length - 1;
  updatePriceSliderUI(container);
};

let priceSliderDebounceTimer = null;

const initPriceSlider = () => {
  const container = getElement("filter-v2-price-range");
  if (!container) return;
  const minInput = container.querySelector(".price-slider-input-min");
  const maxInput = container.querySelector(".price-slider-input-max");
  if (!minInput || !maxInput) return;

  setPriceSliderInitialValues();

  const onInput = () => {
    let minVal = parseInt(minInput.value);
    let maxVal = parseInt(maxInput.value);
    if (minVal > maxVal) {
      if (minInput === document.activeElement) { minInput.value = maxVal; }
      else { maxInput.value = minVal; }
    }
    updatePriceSliderUI(container);
  };

  const onChange = () => {
    updateClearLinkVisibility();
    if (isMobile()) {
      triggerMobileCountUpdate();
    } else {
      clearTimeout(priceSliderDebounceTimer);
      priceSliderDebounceTimer = setTimeout(() => {
        executeFilterSearch(elements.form);
      }, FILTER_INPUT_DEBOUNCE_MS);
    }
  };

  minInput.addEventListener("input", onInput);
  maxInput.addEventListener("input", onInput);
  minInput.addEventListener("change", onChange);
  maxInput.addEventListener("change", onChange);
};

const setInitialValues = () => {
  if (!elements.form) return;
  const inputs = elements.form.querySelectorAll("input");
  const urlParams = getUrlParams();
  const paramValues = urlParams.map((param) => {
    const [name, value] = param.split("=");
    return { name, value: decodeURIComponent(value || "") };
  });

  setInputInitialValues(inputs, paramValues);
  setPriceSliderInitialValues();

  const sortByParam = paramValues.find((param) => param.name === "sort_by");
  const sortDom = getElement("filter-v2-products-sort-selectbox");
  if (sortDom) {
    if (sortByParam) {
      sortDom.value = sortByParam.value;
    } else {
      const firstOption = sortDom.querySelector("option");
      if (firstOption) sortDom.value = firstOption.value;
    }
  }
};

/* ========================================
 * おすすめ商品 / スクロール
 * ======================================== */

const hideFeaturedProducts = () => {
  const urlParams = getUrlParams();
  const url = location.href;
  if (urlParams.length === 0 || !url.includes("filter")) return;
  elements.featuredProductsSection?.classList.add("hide");
  elements.featuredProductsIndex?.classList.add("hide");
};

/* ========================================
 * フィルター URL 生成
 * ======================================== */

const createFilterUrl = (formData) => {
  const url = window.location.pathname;
  const form = new FormData(formData);

  const stockToggle = getElement("filter-v2-stock-toggle");
  if (stockToggle?.checked) {
    form.set("filter.v.availability", "1");
  } else {
    form.delete("filter.v.availability");
  }

  let params = new URLSearchParams(form);
  const query = params.get("q");

  if (!query && url.includes("/search")) {
    form.set("q", "*");
    params = new URLSearchParams(form);
  }

  let urlParams = params.toString();
  urlParams = urlParams.replace(".color", "");
  return `${url}?${urlParams}`;
};

/* ========================================
 * ローディング（部分 fade）
 * ======================================== */

const showLoading = () => {
  const inner = document.querySelector(".collection-products-v2-inner") ??
    document.querySelector(".search-v2-inner");
  if (inner) inner.classList.add("is-loading");
  if (elements.loadingIcon) elements.loadingIcon.classList.add("active");
};

const hideLoading = () => {
  const inner = document.querySelector(".collection-products-v2-inner") ??
    document.querySelector(".search-v2-inner");
  if (inner) inner.classList.remove("is-loading");
  if (elements.loadingIcon) elements.loadingIcon.classList.remove("active");
};

/* ========================================
 * Fetch & 部分更新
 * ======================================== */

const refreshDynamicElements = () => {
  const newSort = getElement("filter-v2-products-sort-selectbox");
  if (newSort && newSort !== elements.productSortDom) {
    newSort.addEventListener("change", handleSortChange);
  }
  elements.productSortDom = newSort;

  const newFilterBtn = getElement("filter-v2-btn");
  if (newFilterBtn && newFilterBtn !== elements.filterBtn) {
    newFilterBtn.addEventListener("click", displayModal);
  }
  elements.filterBtn = newFilterBtn;

  elements.productCountEl = getElement("filter-v2-product-count");
  initStockToggle();

  if (window.themeV2?.resetProductCardImages) {
    window.themeV2.resetProductCardImages();
  }
  if (window.themeV2?.setSwipeProductCardImage) {
    window.themeV2.setSwipeProductCardImage();
  }
};

const fetchAndReplace = async (url, opts = {}) => {
  if (currentFetchController) currentFetchController.abort();
  currentFetchController = new AbortController();

  try {
    const response = await abortableFetch(url, currentFetchController);
    if (!response.ok) throw new Error("Fetch failed");
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newInner = doc.querySelector(".collection-products-v2-inner");
    const currentInner = document.querySelector(".collection-products-v2-inner");
    if (newInner && currentInner) {
      currentInner.replaceWith(document.importNode(newInner, true));
      if (!opts.skipPushState) history.pushState(null, "", url);
      refreshDynamicElements();
      setInitialValues();
      syncStockToggle();
      renderActiveChips();
      updateProductCountDisplay();
      hideFeaturedProducts();
      updateClearLinkVisibility();
    } else {
      window.location.href = url;
    }
  } catch (e) {
    if (e.name === "AbortError") return;
    window.location.href = url;
  } finally {
    hideLoading();
  }
};

/* ========================================
 * フィルター操作
 * ======================================== */

const handleSortChange = (e) => {
  showLoading();
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set("sort_by", e.target.value);
  fetchAndReplace(currentUrl.toString());
};

const executeFilterSearch = (formData) => {
  showLoading();
  const url = createFilterUrl(formData);
  fetchAndReplace(url);
};

let filterInputDebounceTimer = null;

const handleFilterInput = () => {
  if (isMobile()) return;
  clearTimeout(filterInputDebounceTimer);
  filterInputDebounceTimer = setTimeout(() => {
    executeFilterSearch(elements.form);
  }, FILTER_INPUT_DEBOUNCE_MS);
};

let mobileCountDebounceTimer = null;

const triggerMobileCountUpdate = () => {
  if (!isMobile() || !elements.filterUnit?.classList.contains("active")) return;
  clearTimeout(mobileCountDebounceTimer);
  mobileCountDebounceTimer = setTimeout(() => {
    const url = createFilterUrl(elements.form);
    fetchAndUpdateProductCount(url);
  }, MOBILE_COUNT_DEBOUNCE_MS);
};

const handleFilterChange = (e) => {
  const target = e.target;
  if (target.matches("input[type='text']")) {
    handleFilterInput();
    if (isMobile()) triggerMobileCountUpdate();
    return;
  }
  if (target.matches("input[type='checkbox'], input[type='radio'], select")) {
    if (target.id === "eol") {
      const stockToggle = getElement("filter-v2-stock-toggle");
      if (stockToggle) stockToggle.checked = !target.checked;
    }
    updateClearLinkVisibility();
    if (isMobile()) {
      triggerMobileCountUpdate();
    } else {
      executeFilterSearch(elements.form);
    }
  }
};

/**
 * radio のデセレクト対応：選択済み radio をクリックで解除
 */
let _radioClickTarget = null;
let _radioWasChecked = false;

const handleRadioMousedown = (e) => {
  const group = e.target.closest(".filter-v2-group");
  const radio = group?.querySelector('input[type="radio"]');
  if (radio) {
    _radioClickTarget = radio;
    _radioWasChecked = radio.checked;
  } else {
    _radioClickTarget = null;
  }
};

const handleRadioClick = (e) => {
  const group = e.target.closest(".filter-v2-group");
  const radio = group?.querySelector('input[type="radio"]');
  if (radio && radio === _radioClickTarget && _radioWasChecked) {
    radio.checked = false;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }
  _radioClickTarget = null;
  _radioWasChecked = false;
};

/* ========================================
 * クリアリンクの fetch 対応
 * ======================================== */

const buildClearUrl = (baseHref) => {
  const url = new URL(baseHref, window.location.origin);
  const stockToggle = getElement("filter-v2-stock-toggle");
  if (stockToggle?.checked) {
    url.searchParams.set("filter.v.availability", "1");
  }
  return url.toString();
};

const handleClearLinkClick = (e) => {
  const link = e.target.closest("a.filter-v2-link-clear, a.filter-v2-link-clear-pc");
  if (!link) return;
  const href = link.getAttribute("href");
  if (!href) return;

  e.preventDefault();
  const clearUrl = buildClearUrl(href);

  if (isMobile() && elements.filterUnit?.classList.contains("active")) {
    closeModal(false);
    setTimeout(() => {
      showLoading();
      fetchAndReplace(clearUrl);
    }, 0);
    return;
  }

  showLoading();
  fetchAndReplace(clearUrl);
};

/* ========================================
 * PC サイドバー アコーディオン
 * ======================================== */

const initAccordion = () => {
  if (isMobile()) return;
  const form = elements.form;
  if (!form) return;
  form.querySelectorAll(".filter-v2-block-title").forEach((title) => {
    title.addEventListener("click", () => {
      if (isMobile()) return;
      title.closest(".filter-v2-block")?.classList.toggle("is-collapsed");
    });
  });
};

/* ========================================
 * 在庫ありトグル
 * ======================================== */

const syncStockToggle = () => {
  const toggle = getElement("filter-v2-stock-toggle");
  if (!toggle) return;
  const params = new URLSearchParams(window.location.search);
  const isSearch = window.location.pathname.includes("/search");
  if (isSearch && !params.has("filter.v.availability")) {
    toggle.checked = true;
  } else {
    toggle.checked = params.get("filter.v.availability") === "1";
  }
};

const handleStockToggleChange = () => {
  const toggle = getElement("filter-v2-stock-toggle");
  if (!toggle) return;
  const currentUrl = new URL(window.location.href);
  if (toggle.checked) {
    currentUrl.searchParams.set("filter.v.availability", "1");
  } else {
    currentUrl.searchParams.delete("filter.v.availability");
  }
  showLoading();
  fetchAndReplace(currentUrl.toString());
};

const initStockToggle = () => {
  const toggle = getElement("filter-v2-stock-toggle");
  if (!toggle) return;
  syncStockToggle();
  toggle.removeEventListener("change", handleStockToggleChange);
  toggle.addEventListener("change", handleStockToggleChange);
};

/* ========================================
 * 初期化
 * ======================================== */

const initialize = () => {
  elements = getElements();
  if (!elements.form) return;

  if (elements.productSortDom) {
    elements.productSortDom.addEventListener("change", handleSortChange);
  }

  if (elements.filterBtn) {
    elements.filterBtn.addEventListener("click", displayModal);
  }

  if (elements.prevBtn) {
    elements.prevBtn.addEventListener("click", () => closeModal(true));
  }

  if (elements.applyBtn) {
    elements.applyBtn.addEventListener("click", () => {
      executeFilterSearch(elements.form);
      closeModal(false);
    });
  }

  elements.form.addEventListener("change", handleFilterChange);
  elements.form.addEventListener("input", handleFilterChange);
  elements.form.addEventListener("mousedown", handleRadioMousedown);
  elements.form.addEventListener("click", handleRadioClick);

  elements.form.addEventListener("submit", (e) => {
    e.preventDefault();
    executeFilterSearch(elements.form);
  });

  elements.form.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeFilterSearch(elements.form);
    }
  });

  document.addEventListener("click", handleClearLinkClick);

  document.addEventListener("click", (e) => {
    const chip = e.target.closest(".filter-v2-chip");
    if (!chip) return;
    e.preventDefault();
    const url = chip.getAttribute("data-chip-url");
    if (url) {
      showLoading();
      fetchAndReplace(url);
    }
  });

  setInitialValues();
  initPriceSlider();
  initStockToggle();
  initAccordion();
  renderActiveChips();
  hideFeaturedProducts();
  updateClearLinkVisibility();

  window.addEventListener("popstate", () => {
    showLoading();
    fetchAndReplace(window.location.href, { skipPushState: true });
  });

  const productsContainer = getElement("collection-products-v2");
  if (productsContainer) {
    productsContainer.addEventListener("click", (e) => {
      const link = e.target.closest("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;
      if (link.classList.contains("filter-v2-link-clear") ||
          link.classList.contains("filter-v2-link-clear-pc")) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        if (url.pathname !== window.location.pathname) return;
        e.preventDefault();
        showLoading();
        const isPagination = !!link.closest(".pagination");
        fetchAndReplace(url.toString()).then(() => {
          if (isPagination) {
            const target = document.querySelector(".collection-products-v2-list") ||
                           document.querySelector(".collection-products-v2-inner");
            if (target) {
              var headerOffset = 160;
              var top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
              window.scrollTo({ top: top, behavior: "smooth" });
            }
          }
        });
      } catch {
        /* 無効なURLは通常遷移に任せる */
      }
    });
  }
};

document.addEventListener("DOMContentLoaded", initialize);
